/**
 * Τα «συμπεράσματα» της Αρχικής ως καθαρές συναρτήσεις (ελέγχονται χωρίς DOM): ομαδοποίηση καναλιών
 * και πληρωμών με τους τόνους τους, αιχμή, καλύτερη μέρα, συγκέντρωση πωλήσεων, κ.λπ. Το κείμενο το
 * φτιάχνουν οι ενότητες με τα i18n κλειδιά.
 */
import type { CategoryRow, ChannelRow, DayRow, HomeData, HourRow, PaymentRow, ProductRow } from './homeApi';
import type { StackPart, Tone } from './charts/StackedBar';

export const sum = (xs: number[]): number => xs.reduce((s, v) => s + v, 0);
const round2 = (v: number) => Math.round(v * 100) / 100;

const TONES: Tone[] = [1, 2, 3];
const toneAt = (i: number): Tone => TONES[i] ?? 'o';

/* ───────── Δ ───────── */

export type DeltaTone = 'ok' | 'bad' | 'flat' | 'none';

/** Χρώμα ενός Δ: μόνο επιτυχία/κίνδυνος· 0 ουδέτερο, χωρίς σύγκριση «none». */
export function deltaTone(delta: number | null, goodWhenUp: boolean): DeltaTone {
  if (delta === null || !Number.isFinite(delta)) return 'none';
  if (Math.abs(delta) < 0.05) return 'flat';
  return (delta > 0) === goodWhenUp ? 'ok' : 'bad';
}

/**
 * Το πλήθος της σύγκρισης από την τιμή και το Δ% (το συμβόλαιο δίνει μόνο Δ%): 6 με −33,3% → 9.
 * 0 ή −100% → άγνωστο.
 */
export function prevFromDelta(value: number, deltaPct: number | null): number | null {
  if (deltaPct === null || !(value > 0) || deltaPct <= -100) return null;
  const prev = value / (1 + deltaPct / 100);
  return Number.isFinite(prev) ? Math.round(prev) : null;
}

/* ───────── ώρες ───────── */

/**
 * Οι 24 ώρες με τη σειρά της μέρας του μαγαζιού: αρχή αμέσως μετά το μεγαλύτερο (κυκλικά) κενό χωρίς
 * κίνηση — ένα μπαρ που κλείνει 02:00 ξεκινά το απόγευμα και τελειώνει με τις 00–01. Λίστα που δεν
 * είναι ακριβώς 0..23 μένει ως έχει.
 */
export function rotateDay<T extends { hour: number }>(rows: T[], active: (r: T) => boolean): T[] {
  if (rows.length !== 24 || new Set(rows.map(r => r.hour)).size !== 24) return rows;
  const byHour = [...rows].sort((a, b) => a.hour - b.hour);
  const act = byHour.map(active);
  if (!act.some(Boolean) || act.every(Boolean)) return byHour;
  let bestLen = -1;
  let bestEnd = -1;
  for (let start = 0; start < 24; start += 1) {
    if (act[start] || !act[(start + 23) % 24]) continue; // ένα κενό ξεκινά αμέσως μετά από ώρα με κίνηση
    let len = 0;
    while (len < 24 && !act[(start + len) % 24]) len += 1;
    if (len > bestLen) {
      bestLen = len;
      bestEnd = (start + len - 1) % 24;
    }
  }
  const first = (bestEnd + 1) % 24;
  return [...byHour.slice(first), ...byHour.slice(0, first)];
}

/** Από την πρώτη ως την τελευταία ώρα με κίνηση (με τη σειρά της μέρας). */
export function activeHours(rows: HourRow[]): HourRow[] {
  const active = (r: HourRow) => r.orders > 0 || r.revenue > 0;
  const ordered = rotateDay(rows, active);
  const first = ordered.findIndex(active);
  if (first < 0) return [];
  let last = first;
  ordered.forEach((r, i) => { if (active(r)) last = i; });
  return ordered.slice(first, last + 1);
}

export interface PeakWindow {
  /** Δείκτες στη λίστα (συνεχόμενες ώρες). */
  start: number;
  end: number;
  value: number;
  share: number;
}

/** Οι `size` συνεχόμενες ώρες με το μεγαλύτερο άθροισμα (ισοπαλία → η νωρίτερη). */
export function peakWindow(rows: HourRow[], metric: 'orders' | 'revenue', size = 2): PeakWindow | null {
  const vals = rows.map(r => r[metric]);
  const total = sum(vals);
  if (!(total > 0)) return null;
  let best: PeakWindow | null = null;
  for (let i = 0; i < rows.length; i += 1) {
    let end = i;
    let value = vals[i];
    while (end - i + 1 < size && end + 1 < rows.length && rows[end + 1].hour === (rows[end].hour + 1) % 24) {
      end += 1;
      value += vals[end];
    }
    if (!best || value > best.value) best = { start: i, end, value, share: (value / total) * 100 };
  }
  return best;
}

/* ───────── ημέρες ───────── */

export interface DayStats {
  avg: number;
  best: number;
  /** Η τελευταία (επιλεγμένη) μέρα σε σχέση με τον μ.ο., σε %. */
  lastVsAvg: number | null;
}

/**
 * Μέσος όρος, καλύτερη μέρα και «η τελευταία σε σχέση με τον μ.ο.». Με `excludeLast` (σήμερα, μισή μέρα)
 * ο μ.ο. βγαίνει από τις υπόλοιπες, ώστε στις 10:00 να μη «ρίχνει» τον μέσο όρο.
 */
export function dayStats(rows: DayRow[], metric: 'revenue' | 'orders', excludeLast: boolean): DayStats | null {
  if (rows.length === 0) return null;
  const vals = rows.map(r => r[metric]);
  const base = excludeLast && vals.length > 1 ? vals.slice(0, -1) : vals;
  const avg = sum(base) / base.length;
  let best = 0;
  vals.forEach((v, i) => { if (v > vals[best]) best = i; });
  const last = vals[vals.length - 1];
  return { avg, best, lastVsAvg: avg > 0 ? ((last - avg) / avg) * 100 : null };
}

/* ───────── κανάλια ───────── */

const DELIVERY_KEYS = ['delivery', 'efood', 'wolt', 'box'];
const GROUP_ORDER = ['takeaway', 'delivery', 'dine_in'];

export interface ChannelChild { key: string; label: string; orders: number; revenue: number }
export interface ChannelGroup {
  key: string;
  /** Ετικέτα του server (για άγνωστα κανάλια). */
  label: string;
  orders: number;
  revenue: number;
  avg: number;
  share: number;
  tone: Tone;
  children: ChannelChild[];
}

const active = (c: { orders: number; revenue: number }) => c.orders > 0 || c.revenue !== 0;

/**
 * Take Away · Delivery (δικό μας + efood/Wolt/BOX ως «παιδιά») · Τραπέζια — όπως το δείγμα — και ό,τι
 * άλλο στείλει ο server ως δική του γραμμή. Σταθερή σειρά (ίδιο κανάλι = ίδιος τόνος σε κάθε περίοδο).
 */
export function groupChannels(rows: ChannelRow[]): ChannelGroup[] {
  const groups: Array<Omit<ChannelGroup, 'avg' | 'share' | 'tone'>> = [];
  const take = (key: string, label: string, members: ChannelRow[]) => {
    const live = members.filter(active);
    if (live.length === 0) return;
    const showChildren = live.length > 1 || (live.length === 1 && live[0].key !== key);
    groups.push({
      key,
      label,
      orders: sum(live.map(m => m.orders)),
      revenue: round2(sum(live.map(m => m.revenue))),
      children: showChildren ? live.map(m => ({ key: m.key, label: m.label, orders: m.orders, revenue: m.revenue })) : [],
    });
  };
  take('takeaway', rows.find(r => r.key === 'takeaway')?.label || 'Take Away', rows.filter(r => r.key === 'takeaway'));
  take('delivery', 'Delivery', DELIVERY_KEYS.flatMap(k => rows.filter(r => r.key === k)));
  take('dine_in', rows.find(r => r.key === 'dine_in')?.label || '', rows.filter(r => r.key === 'dine_in'));
  rows.filter(r => !GROUP_ORDER.includes(r.key) && !DELIVERY_KEYS.includes(r.key)).forEach(r => take(r.key, r.label, [r]));
  const total = sum(groups.map(g => Math.max(0, g.revenue)));
  return groups.map((g, i) => ({
    ...g,
    avg: g.orders > 0 ? g.revenue / g.orders : 0,
    share: total > 0 ? (Math.max(0, g.revenue) / total) * 100 : 0,
    tone: toneAt(i),
  }));
}

/* ───────── πληρωμές ───────── */

const PAY_ORDER = ['cash', 'card', 'online', 'iris', 'credit', 'deposit', 'other'];

export interface PayLine extends PaymentRow {
  tone: Tone;
  share: number;
}

/** Σταθερή σειρά (μετρητά, κάρτα, online, …)· οι τρεις πρώτοι με ποσό παίρνουν τους τόνους, οι υπόλοιποι γκρι. */
export function paymentLines(rows: PaymentRow[]): PayLine[] {
  const rank = (k: string) => {
    const i = PAY_ORDER.indexOf(k);
    return i < 0 ? PAY_ORDER.length : i;
  };
  const live = rows.filter(r => r.amount !== 0).sort((a, b) => rank(a.key) - rank(b.key));
  const total = sum(live.map(r => Math.max(0, r.amount)));
  return live.map((r, i) => ({ ...r, tone: toneAt(i), share: total > 0 ? (Math.max(0, r.amount) / total) * 100 : 0 }));
}

/** Η μπάρα των πληρωμών: έως τρία χρώματα + ένα γκρι κομμάτι για «τα υπόλοιπα». */
export function paymentStack(lines: PayLine[], label: (key: string, server: string) => string, otherJoin = ' + '): StackPart[] {
  const main = lines.filter(l => l.tone !== 'o').map(l => ({ key: l.key, label: label(l.key, l.label), value: Math.max(0, l.amount), tone: l.tone }));
  const rest = lines.filter(l => l.tone === 'o' && l.amount > 0);
  if (rest.length === 0) return main;
  return [...main, { key: 'rest', label: rest.map(l => label(l.key, l.label)).join(otherJoin), value: sum(rest.map(l => l.amount)), tone: 'o' as Tone }];
}

/* ───────── προϊόντα / κατηγορίες ───────── */

export interface Concentration {
  top3: number;
  next7: number;
  rest: number;
  /** % των εισπράξεων προϊόντων από τα 10 πρώτα. */
  top10Share: number;
}

export function concentration(top: ProductRow[], productRevenue: number): Concentration | null {
  if (!(productRevenue > 0) || top.length === 0) return null;
  const sorted = [...top].sort((a, b) => b.revenue - a.revenue).slice(0, 10);
  const top3 = sum(sorted.slice(0, 3).map(p => p.revenue));
  const next7 = sum(sorted.slice(3).map(p => p.revenue));
  return { top3, next7, rest: Math.max(0, productRevenue - top3 - next7), top10Share: ((top3 + next7) / productRevenue) * 100 };
}

/** Κατηγορία με πολλά τεμάχια αλλά λίγα έσοδα (διαφορά μεριδίων ≥ 8 μονάδες). */
export function unitsGap(cats: CategoryRow[]): { cat: CategoryRow; unitShare: number; revenueShare: number } | null {
  const units = sum(cats.map(c => c.quantity));
  const revenue = sum(cats.map(c => c.revenue));
  if (!(units > 0) || !(revenue > 0)) return null;
  let best: { cat: CategoryRow; unitShare: number; revenueShare: number } | null = null;
  for (const cat of cats) {
    const unitShare = (cat.quantity / units) * 100;
    const revenueShare = (cat.revenue / revenue) * 100;
    if (unitShare - revenueShare >= 8 && (!best || unitShare - revenueShare > best.unitShare - best.revenueShare)) {
      best = { cat, unitShare, revenueShare };
    }
  }
  return best;
}

/* ───────── προσωπικό ───────── */

export type StaffRole = 'cashier' | 'waiter' | 'driver' | 'platforms';

export interface StaffLine {
  key: string;
  name: string;
  role: StaffRole;
  orders: number;
  revenue: number;
  muted?: boolean;
}

const PLATFORM_KEYS = ['efood', 'wolt', 'box'];
/** Ίδια γραφή με τα «παιδιά» του Delivery στα Κανάλια. */
export const PLATFORM_NAMES: Record<string, string> = { efood: 'efood', wolt: 'Wolt', box: 'BOX' };

/** Πλατφόρμες της περιόδου (από τα κανάλια): για τη γραμμή «Πλατφόρμες» του Προσωπικού. */
export function platformTotals(channels: ChannelRow[]): { orders: number; revenue: number; names: string[] } {
  const rows = channels.filter(c => PLATFORM_KEYS.includes(c.key) && active(c));
  return { orders: sum(rows.map(r => r.orders)), revenue: round2(sum(rows.map(r => r.revenue))), names: rows.map(r => PLATFORM_NAMES[r.key] || r.label || r.key) };
}

/* ───────── ακυρώσεις ───────── */

/**
 * Λίστες «ετικέτα → πλήθος» από διαφορετικές ομάδες (παραγγελίες, είδη τραπεζιών, ολόκληρα τραπέζια) σε μία: ίδια
 * ετικέτα αθροίζεται (ο ίδιος χρήστης, «Χωρίς χρήστη», ίδια αιτία), κατά πλήθος και μετά αλφαβητικά.
 */
export function mergeCounts(lists: Array<Array<{ label: string; count: number }>>): Array<{ label: string; count: number }> {
  const out = new Map<string, number>();
  for (const list of lists) {
    for (const r of list) if (r.count > 0) out.set(r.label, (out.get(r.label) || 0) + r.count);
  }
  return Array.from(out.entries(), ([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, 'el'));
}

/** Ταμίες, σερβιτόροι, διανομείς (ο καθένας κατά εισπράξεις)· χωρίς κίνηση → εκτός. */
export function staffLines(staff: HomeData['staff']): StaffLine[] {
  const pick = (rows: HomeData['staff']['cashiers'], role: StaffRole): StaffLine[] =>
    rows.filter(active)
      .sort((a, b) => b.revenue - a.revenue)
      .map((r, i) => ({ key: `${role}-${r.employee_id ?? `x${i}`}`, name: r.name, role, orders: r.orders, revenue: r.revenue }));
  return [...pick(staff.cashiers, 'cashier'), ...pick(staff.waiters, 'waiter'), ...pick(staff.drivers, 'driver')];
}
